import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useBaseUrl } from '../../../utils/utils';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

interface MedicalRecord {
    id: string;
    record_identifier: string;
    name: string;
    status: string;
    privacy_level: string;
    date_created: string;
    date_of_service: string;
    version: number;
    patient: {
        patient_id: string;
        first_name: string;
        last_name: string;
        email: string;
        gender: string;
        date_of_birth: string;
    };
    examined_by: {
        fname: string;
        lname: string;
        title: {
            name: string;
        };
    };
    chief_complaint: string;
    history_present_illness?: string;
    past_medical_history?: string;
    social_history?: string;
    family_history?: string;
    current_medications?: string;
    known_allergies?: string;
    physical_examination?: string;
    clinical_assessment?: string;
    treatment_plan?: string;
    diagnosis_code?: string;
    diagnosis_description?: string;
    provider_notes?: string;
    provider_signature: boolean;
    medications: any[];
    vital_signs: any[];
    lab_results: any[];
    imaging_results: any[];
    treatment_follow_ups: any[];
    uploaded_documents: any[];
    emergency_contact_name?: string;
    emergency_contact_phone?: string;
    emergency_contact_relationship?: string;
    insurance_information?: string;
    is_confidential: boolean;
    requires_follow_up: boolean;
    created_by?: {
        username: string;
        first_name: string;
        last_name: string;
    };
    updated_by?: {
        username: string;
        first_name: string;
        last_name: string;
    };
}

interface EHRRecordViewerProps {
    recordId: string;
    isVisible: boolean;
    onClose: () => void;
    onRecordUpdated?: () => void;
}

const EHRRecordViewer: React.FC<EHRRecordViewerProps> = ({
    recordId,
    isVisible,
    onClose,
    onRecordUpdated
}) => {
    const [record, setRecord] = useState<MedicalRecord | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<string>("overview");
    const [showAddSegment, setShowAddSegment] = useState<boolean>(false);
    const [segmentType, setSegmentType] = useState<string>("");
    const [isAddingSegment, setIsAddingSegment] = useState<boolean>(false);
    const [editHistory, setEditHistory] = useState<any[]>([]);
    const [showHistory, setShowHistory] = useState<boolean>(false);
    
    // Form data for new segments
    const [newSegmentData, setNewSegmentData] = useState<any>({});
    
    const base_url = useBaseUrl();

    useEffect(() => {
        if (isVisible && recordId) {
            loadRecord();
        }
    }, [isVisible, recordId]);

    const loadRecord = async () => {
        setIsLoading(true);
        try {
            const url = `${base_url}ehr/api/v1/medical-records/${recordId}/comprehensive/`;

            const response = await axios.get(url, {
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            const { status, data } = response.data;

            if (status === "success" && data) {
                setRecord(data);
            } else {
                throw new Error("Failed to load record");
            }
        } catch (error: any) {
            console.error("💥 Error loading record:", error);
            await Swal.fire({
                icon: "error",
                title: "Error Loading Record",
                text: error.message || "Failed to load the medical record"
            });
        } finally {
            setIsLoading(false);
        }
    };

    const loadEditHistory = async () => {
        try {
            const url = `${base_url}ehr/api/v1/medical-records/${recordId}/history/`;
            const response = await axios.get(url);
            
            const { status, data } = response.data;
            if (status === "success") {
                setEditHistory(data);
                setShowHistory(true);
            }
        } catch (error: any) {
            console.error("Error loading edit history:", error);
            await Swal.fire({
                icon: "error",
                title: "Error",
                text: "Failed to load edit history"
            });
        }
    };

    const handleAddSegment = async () => {
        if (!segmentType || !record) {
            await Swal.fire({
                icon: "warning",
                title: "Missing Information",
                text: "Please select a segment type and fill in the required information"
            });
            return;
        }

        setIsAddingSegment(true);
        try {
            const url = `${base_url}ehr/api/v1/medical-records/${record.id}/add_segment/`;
            
            const response = await axios.post(url, {
                segment_type: segmentType,
                segment_data: newSegmentData
            }, {
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            const { status, data } = response.data;

            if (status === "success") {
                setRecord(data);
                setShowAddSegment(false);
                setSegmentType("");
                setNewSegmentData({});
                
                await Swal.fire({
                    icon: "success",
                    title: "Segment Added",
                    text: "The new segment has been successfully added to the record",
                    timer: 2000,
                    showConfirmButton: false
                });

                if (onRecordUpdated) {
                    onRecordUpdated();
                }
            } else {
                throw new Error("Failed to add segment");
            }
        } catch (error: any) {
            console.error("Error adding segment:", error);
            await Swal.fire({
                icon: "error",
                title: "Error Adding Segment",
                text: error.message || "Failed to add the new segment"
            });
        } finally {
            setIsAddingSegment(false);
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const renderSegmentForm = () => {
        switch (segmentType) {
            case 'medication':
                return (
                    <div className="row g-3">
                        <div className="col-md-6">
                            <label className="form-label">Medication Name *</label>
                            <input
                                type="text"
                                className="form-control"
                                value={newSegmentData.name || ''}
                                onChange={(e) => setNewSegmentData({...newSegmentData, name: e.target.value})}
                                required
                            />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Dosage *</label>
                            <input
                                type="text"
                                className="form-control"
                                value={newSegmentData.dosage || ''}
                                onChange={(e) => setNewSegmentData({...newSegmentData, dosage: e.target.value})}
                                required
                            />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Frequency</label>
                            <input
                                type="text"
                                className="form-control"
                                value={newSegmentData.frequency || ''}
                                onChange={(e) => setNewSegmentData({...newSegmentData, frequency: e.target.value})}
                                placeholder="e.g., Twice daily"
                            />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Duration</label>
                            <input
                                type="text"
                                className="form-control"
                                value={newSegmentData.dosage_duration || ''}
                                onChange={(e) => setNewSegmentData({...newSegmentData, dosage_duration: e.target.value})}
                                placeholder="e.g., 7 days"
                            />
                        </div>
                        <div className="col-12">
                            <label className="form-label">Instructions</label>
                            <textarea
                                className="form-control"
                                rows={3}
                                value={newSegmentData.recommendation || ''}
                                onChange={(e) => setNewSegmentData({...newSegmentData, recommendation: e.target.value})}
                                placeholder="Additional instructions for the patient"
                            />
                        </div>
                    </div>
                );
            
            case 'vital_signs':
                return (
                    <div className="row g-3">
                        <div className="col-md-6">
                            <label className="form-label">Temperature (°C)</label>
                            <input
                                type="number"
                                step="0.1"
                                className="form-control"
                                value={newSegmentData.temperature || ''}
                                onChange={(e) => setNewSegmentData({...newSegmentData, temperature: e.target.value})}
                            />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Heart Rate (bpm)</label>
                            <input
                                type="number"
                                className="form-control"
                                value={newSegmentData.heart_rate || ''}
                                onChange={(e) => setNewSegmentData({...newSegmentData, heart_rate: e.target.value})}
                            />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Blood Pressure</label>
                            <input
                                type="text"
                                className="form-control"
                                value={newSegmentData.blood_pressure || ''}
                                onChange={(e) => setNewSegmentData({...newSegmentData, blood_pressure: e.target.value})}
                                placeholder="e.g., 120/80"
                            />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Weight (kg)</label>
                            <input
                                type="number"
                                step="0.1"
                                className="form-control"
                                value={newSegmentData.weight || ''}
                                onChange={(e) => setNewSegmentData({...newSegmentData, weight: e.target.value})}
                            />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Height (cm)</label>
                            <input
                                type="number"
                                className="form-control"
                                value={newSegmentData.height || ''}
                                onChange={(e) => setNewSegmentData({...newSegmentData, height: e.target.value})}
                            />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Oxygen Saturation (%)</label>
                            <input
                                type="number"
                                className="form-control"
                                value={newSegmentData.oxygen_saturation || ''}
                                onChange={(e) => setNewSegmentData({...newSegmentData, oxygen_saturation: e.target.value})}
                            />
                        </div>
                        <div className="col-12">
                            <label className="form-label">Notes</label>
                            <textarea
                                className="form-control"
                                rows={3}
                                value={newSegmentData.vital_notes || ''}
                                onChange={(e) => setNewSegmentData({...newSegmentData, vital_notes: e.target.value})}
                            />
                        </div>
                    </div>
                );
            
            case 'lab_result':
                return (
                    <div className="row g-3">
                        <div className="col-md-6">
                            <label className="form-label">Test Name *</label>
                            <input
                                type="text"
                                className="form-control"
                                value={newSegmentData.test_name || ''}
                                onChange={(e) => setNewSegmentData({...newSegmentData, test_name: e.target.value})}
                                required
                            />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Result *</label>
                            <input
                                type="text"
                                className="form-control"
                                value={newSegmentData.result || ''}
                                onChange={(e) => setNewSegmentData({...newSegmentData, result: e.target.value})}
                                required
                            />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Reference Range</label>
                            <input
                                type="text"
                                className="form-control"
                                value={newSegmentData.reference_range || ''}
                                onChange={(e) => setNewSegmentData({...newSegmentData, reference_range: e.target.value})}
                            />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Units</label>
                            <input
                                type="text"
                                className="form-control"
                                value={newSegmentData.units || ''}
                                onChange={(e) => setNewSegmentData({...newSegmentData, units: e.target.value})}
                            />
                        </div>
                        <div className="col-12">
                            <label className="form-label">Comments</label>
                            <textarea
                                className="form-control"
                                rows={3}
                                value={newSegmentData.comments || ''}
                                onChange={(e) => setNewSegmentData({...newSegmentData, comments: e.target.value})}
                            />
                        </div>
                    </div>
                );
            
            case 'treatment_follow_up':
                return (
                    <div className="row g-3">
                        <div className="col-12">
                            <label className="form-label">Observation *</label>
                            <textarea
                                className="form-control"
                                rows={3}
                                value={newSegmentData.observation || ''}
                                onChange={(e) => setNewSegmentData({...newSegmentData, observation: e.target.value})}
                                required
                            />
                        </div>
                        <div className="col-12">
                            <label className="form-label">Notes</label>
                            <textarea
                                className="form-control"
                                rows={3}
                                value={newSegmentData.notes || ''}
                                onChange={(e) => setNewSegmentData({...newSegmentData, notes: e.target.value})}
                            />
                        </div>
                        <div className="col-12">
                            <label className="form-label">Conclusion</label>
                            <textarea
                                className="form-control"
                                rows={3}
                                value={newSegmentData.conclusion || ''}
                                onChange={(e) => setNewSegmentData({...newSegmentData, conclusion: e.target.value})}
                            />
                        </div>
                    </div>
                );
            
            case 'provider_notes':
                return (
                    <div className="row g-3">
                        <div className="col-12">
                            <label className="form-label">Additional Notes *</label>
                            <CKEditor
                                editor={ClassicEditor}
                                data={newSegmentData.notes || ''}
                                onChange={(event, editor) => {
                                    const data = editor.getData();
                                    setNewSegmentData({...newSegmentData, notes: data});
                                }}
                                config={{
                                    toolbar: ['heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', '|', 'outdent', 'indent', '|', 'blockQuote', 'insertTable', '|', 'undo', 'redo']
                                }}
                            />
                        </div>
                    </div>
                );
            
            default:
                return <div className="alert alert-warning">Please select a segment type.</div>;
        }
    };

    if (!isVisible) return null;

    return (
        <div className="modal fade show d-block" tabIndex={-1} style={{ background: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl modal-dialog-scrollable">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            <i className="ri-file-text-line me-2"></i>
                            {record?.name || 'Medical Record'}
                            {record?.version && record.version > 1 && (
                                <span className="badge bg-warning-subtle text-warning ms-2">
                                    Version {record.version}
                                </span>
                            )}
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    
                    <div className="modal-body">
                        {isLoading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3 text-muted">Loading medical record...</p>
                            </div>
                        ) : record ? (
                            <>
                                {/* Record Header */}
                                <div className="card mb-4">
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-md-8">
                                                <h6 className="card-title mb-3">Patient Information</h6>
                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <p><strong>Patient:</strong> {record.patient.first_name} {record.patient.last_name}</p>
                                                        <p><strong>Patient ID:</strong> {record.patient.patient_id}</p>
                                                        <p><strong>Gender:</strong> {record.patient.gender}</p>
                                                    </div>
                                                    <div className="col-md-6">
                                                                                                <p><strong>Date of Birth:</strong> {formatDate(record.patient.date_of_birth)}</p>
                                        <p><strong>Email:</strong> {record.patient.email}</p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <h6 className="card-title mb-3">Record Details</h6>
                                                <p><strong>Record ID:</strong> {record.record_identifier}</p>
                                                <p><strong>Status:</strong> 
                                                    <span className={`badge ms-2 ${
                                                        record.status === 'active' ? 'bg-success' : 
                                                        record.status === 'completed' ? 'bg-primary' : 
                                                        record.status === 'draft' ? 'bg-warning' : 'bg-secondary'
                                                    }`}>
                                                        {record.status.charAt(0).toUpperCase() + record.status.slice(1)}
                                                    </span>
                                                </p>
                                                <p><strong>Privacy:</strong> 
                                                    <span className={`badge ms-2 ${
                                                        record.privacy_level === 'confidential' ? 'bg-danger' : 
                                                        record.privacy_level === 'restricted' ? 'bg-warning' : 'bg-info'
                                                    }`}>
                                                        {record.privacy_level.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                    </span>
                                                </p>
                                                <p><strong>Service Date:</strong> {formatDate(record.date_of_service)}</p>
                                                <p><strong>Examined by:</strong> {record.examined_by.title.name}. {record.examined_by.fname} {record.examined_by.lname}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Tabs Navigation */}
                                <ul className="nav nav-tabs mb-4" role="tablist">
                                    <li className="nav-item" role="presentation">
                                        <button 
                                            className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('overview')}
                                        >
                                            <i className="ri-file-text-line me-2"></i>Overview
                                        </button>
                                    </li>
                                    <li className="nav-item" role="presentation">
                                        <button 
                                            className={`nav-link ${activeTab === 'medications' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('medications')}
                                        >
                                            <i className="ri-capsule-line me-2"></i>
                                            Medications ({record.medications?.length || 0})
                                        </button>
                                    </li>
                                    <li className="nav-item" role="presentation">
                                        <button 
                                            className={`nav-link ${activeTab === 'vitals' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('vitals')}
                                        >
                                            <i className="ri-heart-pulse-line me-2"></i>
                                            Vitals ({record.vital_signs?.length || 0})
                                        </button>
                                    </li>
                                    <li className="nav-item" role="presentation">
                                        <button 
                                            className={`nav-link ${activeTab === 'labs' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('labs')}
                                        >
                                            <i className="ri-test-tube-line me-2"></i>
                                            Lab Results ({record.lab_results?.length || 0})
                                        </button>
                                    </li>
                                    <li className="nav-item" role="presentation">
                                        <button 
                                            className={`nav-link ${activeTab === 'follow_ups' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('follow_ups')}
                                        >
                                            <i className="ri-calendar-check-line me-2"></i>
                                            Follow-ups ({record.treatment_follow_ups?.length || 0})
                                        </button>
                                    </li>
                                </ul>

                                {/* Tab Content */}
                                <div className="tab-content">
                                    {activeTab === 'overview' && (
                                        <div className="tab-pane fade show active">
                                            <div className="row g-4">
                                                <div className="col-12">
                                                    <div className="card">
                                                        <div className="card-header">
                                                            <h6 className="card-title mb-0">Chief Complaint & Assessment</h6>
                                                        </div>
                                                        <div className="card-body">
                                                            <div className="mb-3">
                                                                <strong>Chief Complaint:</strong>
                                                                <p className="mt-2">{record.chief_complaint}</p>
                                                            </div>
                                                            {record.history_present_illness && (
                                                                <div className="mb-3">
                                                                    <strong>History of Present Illness:</strong>
                                                                    <p className="mt-2">{record.history_present_illness}</p>
                                                                </div>
                                                            )}
                                                            {record.clinical_assessment && (
                                                                <div className="mb-3">
                                                                    <strong>Clinical Assessment:</strong>
                                                                    <p className="mt-2">{record.clinical_assessment}</p>
                                                                </div>
                                                            )}
                                                            {record.treatment_plan && (
                                                                <div className="mb-3">
                                                                    <strong>Treatment Plan:</strong>
                                                                    <p className="mt-2">{record.treatment_plan}</p>
                                                                </div>
                                                            )}
                                                            {record.diagnosis_description && (
                                                                <div className="mb-3">
                                                                    <strong>Diagnosis:</strong>
                                                                    <p className="mt-2">{record.diagnosis_description}</p>
                                                                    {record.diagnosis_code && (
                                                                        <small className="text-muted">Code: {record.diagnosis_code}</small>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                {record.provider_notes && (
                                                    <div className="col-12">
                                                        <div className="card">
                                                            <div className="card-header">
                                                                <h6 className="card-title mb-0">Provider Notes</h6>
                                                            </div>
                                                            <div className="card-body">
                                                                <div dangerouslySetInnerHTML={{ __html: record.provider_notes }} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {activeTab === 'medications' && (
                                        <div className="tab-pane fade show active">
                                            {record.medications && record.medications.length > 0 ? (
                                                <div className="row g-3">
                                                    {record.medications.map((medication, index) => (
                                                        <div key={index} className="col-md-6">
                                                            <div className="card">
                                                                <div className="card-body">
                                                                    <h6 className="card-title">{medication.name}</h6>
                                                                    <p className="card-text">
                                                                        <strong>Dosage:</strong> {medication.dosage}<br/>
                                                                        <strong>Frequency:</strong> {medication.frequency}<br/>
                                                                        {medication.dosage_duration && <><strong>Duration:</strong> {medication.dosage_duration}<br/></>}
                                                                        {medication.recommendation && <><strong>Instructions:</strong> {medication.recommendation}</>}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="text-center py-4">
                                                    <i className="ri-capsule-line fs-1 text-muted"></i>
                                                    <p className="text-muted mt-2">No medications recorded</p>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {activeTab === 'vitals' && (
                                        <div className="tab-pane fade show active">
                                            {record.vital_signs && record.vital_signs.length > 0 ? (
                                                <div className="row g-3">
                                                    {record.vital_signs.map((vital, index) => (
                                                        <div key={index} className="col-12">
                                                            <div className="card">
                                                                <div className="card-body">
                                                                    <div className="row">
                                                                        <div className="col-md-3">
                                                                            <strong>Temperature:</strong> {vital.temperature}°C
                                                                        </div>
                                                                        <div className="col-md-3">
                                                                            <strong>Heart Rate:</strong> {vital.heart_rate} bpm
                                                                        </div>
                                                                        <div className="col-md-3">
                                                                            <strong>Blood Pressure:</strong> {vital.blood_pressure}
                                                                        </div>
                                                                        <div className="col-md-3">
                                                                            <strong>Weight:</strong> {vital.weight} kg
                                                                        </div>
                                                                    </div>
                                                                    {vital.vital_notes && (
                                                                        <div className="mt-3">
                                                                            <strong>Notes:</strong> {vital.vital_notes}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="text-center py-4">
                                                    <i className="ri-heart-pulse-line fs-1 text-muted"></i>
                                                    <p className="text-muted mt-2">No vital signs recorded</p>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {activeTab === 'labs' && (
                                        <div className="tab-pane fade show active">
                                            {record.lab_results && record.lab_results.length > 0 ? (
                                                <div className="table-responsive">
                                                    <table className="table table-striped">
                                                        <thead>
                                                            <tr>
                                                                <th>Test Name</th>
                                                                <th>Result</th>
                                                                <th>Reference Range</th>
                                                                <th>Comments</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {record.lab_results.map((lab, index) => (
                                                                <tr key={index}>
                                                                    <td>{lab.test_name}</td>
                                                                    <td>{lab.result}</td>
                                                                    <td>{lab.reference_range}</td>
                                                                    <td>{lab.comments}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            ) : (
                                                <div className="text-center py-4">
                                                    <i className="ri-test-tube-line fs-1 text-muted"></i>
                                                    <p className="text-muted mt-2">No lab results recorded</p>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {activeTab === 'follow_ups' && (
                                        <div className="tab-pane fade show active">
                                            {record.treatment_follow_ups && record.treatment_follow_ups.length > 0 ? (
                                                <div className="row g-3">
                                                    {record.treatment_follow_ups.map((followUp, index) => (
                                                        <div key={index} className="col-12">
                                                            <div className="card">
                                                                <div className="card-body">
                                                                    <h6 className="card-title">Follow-up #{index + 1}</h6>
                                                                    <div className="mb-2">
                                                                        <strong>Observation:</strong>
                                                                        <p className="mt-1">{followUp.observation}</p>
                                                                    </div>
                                                                    {followUp.notes && (
                                                                        <div className="mb-2">
                                                                            <strong>Notes:</strong>
                                                                            <p className="mt-1">{followUp.notes}</p>
                                                                        </div>
                                                                    )}
                                                                    {followUp.conclusion && (
                                                                        <div className="mb-2">
                                                                            <strong>Conclusion:</strong>
                                                                            <p className="mt-1">{followUp.conclusion}</p>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="text-center py-4">
                                                    <i className="ri-calendar-check-line fs-1 text-muted"></i>
                                                    <p className="text-muted mt-2">No follow-ups recorded</p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </>
                        ) : (
                            <div className="text-center py-5">
                                <div className="alert alert-warning">
                                    <i className="ri-alert-line me-2"></i>
                                    Failed to load the medical record.
                                </div>
                            </div>
                        )}
                    </div>
                    
                    <div className="modal-footer">
                        <div className="d-flex justify-content-between w-100">
                            <div>
                                {record && record.version > 1 && (
                                    <button 
                                        type="button" 
                                        className="btn btn-outline-info"
                                        onClick={loadEditHistory}
                                    >
                                        <i className="ri-history-line me-2"></i>
                                        View Edit History
                                    </button>
                                )}
                            </div>
                            <div>
                                <button type="button" className="btn btn-secondary me-2" onClick={onClose}>
                                    Close
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-primary"
                                    onClick={() => setShowAddSegment(true)}
                                >
                                    <i className="ri-add-line me-2"></i>
                                    Add Segment
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Add Segment Modal */}
            {showAddSegment && (
                <div className="modal fade show d-block" tabIndex={-1} style={{ background: 'rgba(0,0,0,0.7)', zIndex: 1060 }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <i className="ri-add-circle-line me-2"></i>
                                    Add New Segment
                                </h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={() => {
                                        setShowAddSegment(false);
                                        setSegmentType("");
                                        setNewSegmentData({});
                                    }}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-4">
                                    <label className="form-label">Segment Type *</label>
                                    <select 
                                        className="form-select"
                                        value={segmentType}
                                        onChange={(e) => {
                                            setSegmentType(e.target.value);
                                            setNewSegmentData({});
                                        }}
                                    >
                                        <option value="">Select segment type...</option>
                                        <option value="medication">Medication</option>
                                        <option value="vital_signs">Vital Signs</option>
                                        <option value="lab_result">Lab Result</option>
                                        <option value="treatment_follow_up">Treatment Follow-up</option>
                                        <option value="provider_notes">Provider Notes</option>
                                    </select>
                                </div>

                                {segmentType && renderSegmentForm()}
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary"
                                    onClick={() => {
                                        setShowAddSegment(false);
                                        setSegmentType("");
                                        setNewSegmentData({});
                                    }}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-primary"
                                    onClick={handleAddSegment}
                                    disabled={isAddingSegment || !segmentType}
                                >
                                    {isAddingSegment ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                            Adding...
                                        </>
                                    ) : (
                                        <>
                                            <i className="ri-save-line me-2"></i>
                                            Add Segment
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit History Modal */}
            {showHistory && (
                <div className="modal fade show d-block" tabIndex={-1} style={{ background: 'rgba(0,0,0,0.7)', zIndex: 1060 }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <i className="ri-history-line me-2"></i>
                                    Edit History
                                </h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={() => setShowHistory(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                {editHistory.length > 0 ? (
                                    <div className="timeline">
                                        {editHistory.map((history, index) => (
                                            <div key={index} className="timeline-item mb-3">
                                                <div className="card">
                                                    <div className="card-body">
                                                        <div className="d-flex justify-content-between">
                                                            <h6 className="card-title">Version {history.version}</h6>
                                                            <small className="text-muted">{formatDate(history.changed_at)}</small>
                                                        </div>
                                                        <p className="card-text">
                                                            <strong>Changed by:</strong> {history.changed_by?.first_name} {history.changed_by?.last_name}<br/>
                                                            <strong>Reason:</strong> {history.change_reason}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-4">
                                        <i className="ri-history-line fs-1 text-muted"></i>
                                        <p className="text-muted mt-2">No edit history available</p>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowHistory(false)}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EHRRecordViewer;
